import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { lycopersicumSlice } from '../../features/lycopersicum/lycopersicumSlice'
import SlotControl from './LycopersicumSlotControl'
import TimerControls from './LycopersicumControls'
import timerBeep from '../../assets/sounds/timer.wav'
import clsx from 'clsx'
import {
  makeStyles,
  Container,
  Box,
  Typography,
} from '@material-ui/core'
import {
  LocalCafe as LocalCafeIcon,
  Work as WorkIcon,
} from '@material-ui/icons'

const flexColumn = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
}

const baseSize = 'calc(6rem + 18vmin)'
const baseSizeLandscape = 'calc(3rem + 18vmin)'
const landscapeQuery = `@media screen and (max-width: 960px) and (orientation: landscape),
                        screen and (max-height: 600px) and (orientation: landscape)`

const useStyles = makeStyles(theme => ({
  container: {
    ...flexColumn,
    position: 'absolute',
    textAlign: 'center',
    maxWidth: '100vw',
    minHeight: '100vh',
    color: theme.palette.primary.contrastText,
    backgroundColor: '#242B33',
    [landscapeQuery]: {
      flexFlow: 'row wrap',
    },
  },
  titles: {
    display: 'block',
    [landscapeQuery]: {
      ...flexColumn,
      width: '100vw',
      flexGrow: 1,
      overflow: 'auto',
      flexDirection: 'row',
      justifyContent: 'space-between',
      '& h1': {
        fontSize: '1rem',
      },
      '& h2': {
        fontSize: '1rem',
      },
    },
  },
  timer: {
    ...flexColumn,
    margin: '1rem',
    padding: '2rem',
    border: '6px solid',
    borderColor: theme.palette.primary.contrastText,
    height: baseSize,
    width: baseSize,
    borderRadius: 'calc(6rem + 9vmin)',
    justifyContent: 'flex-start',
    [landscapeQuery]: {
      height: baseSizeLandscape,
      width: baseSizeLandscape,
      borderRadius: baseSizeLandscape,
    },
  },
  timerDisplay: {
    margin: '0',
    padding: '1rem',
    fontSize: 'calc(4rem + 6vmin)',
    fontWeight: 'bolder',
    fontFamily: 'Digital-7',
    lineHeight: '1',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
    [landscapeQuery]: {
      padding: 0,
      fontSize: '3.5rem',
    },
  },
  timerLabel: {
    marginTop: 'calc((6rem + 18vmin)/24)',
    fontSize: 'calc(2rem + 2vmin)',
    [landscapeQuery]: {
      margin: 0,
      fontSize: '2rem',
    },
  },
  timerActive: {
    animation: '$blinkColon 1s step-start infinite',
  },
  '@keyframes blinkColon ': {
    '0%': {
      opacity: 0,
    },
    '50%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
    },
  },
  sessionControls: {
    ...flexColumn,
    maxWidth: '480px',
    justifyContent: 'space-between',
    flexDirection: 'row',
    [landscapeQuery]: {
      margin: 0,
      maxWidth: 'max-content',
    },
  },
  slotControl: {
    ...flexColumn,
    minWidth: '100px',
  },
  slotLength: {
    display: 'block',
    textAlign: 'center',
    width: '36px',
    height: '36px',
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(4),
    border: '3px solid',
    borderColor: theme.palette.primary.contrastText,
    fontFamily: 'Digital-7',
    fontWeight: 'bolder',
  },
  timerControls: {
    ...flexColumn,
    marginTop: theme.spacing(4),
    justifyContent: 'space-between',
    flexDirection: 'row',
    width: baseSize,
    [landscapeQuery]: {
      flexDirection: 'column',
      margin: 'auto 0 auto 24px',
      width: 'max-content',
    }
  },
  iconControl: {
    color: theme.palette.primary.contrastText,
    '&:hover': {
      color: '#F8EEC8',
      cursor: 'pointer',
    },
    [landscapeQuery]: {
      margin: '8px 0',
    }
  },
  iconDisabled: {
    color: '#666',
  },
  iconActive: {
    color: '#FF9',
  },
}))

const Lycopersicum = props => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const {
    increaseLength,
    decreaseLength,
    changeActiveCounter,
    countDown,
    resetCounter
  } = lycopersicumSlice.actions
  const state = useSelector(state => state.lycopersicum)
  const [timerActive, setTimerActive] = useState(false)
  const [beepActive, setBeepActive] = useState(false)
  const beepRef = useRef()

  const getMinutes = () => {
    let minutes = Math.floor(state.timer / 60)
    return minutes < 10 ? '0' + minutes : minutes
  }

  const getSeconds = () => {
    let seconds = state.timer % 60
    return seconds < 10 ? '0' + seconds : seconds
  }

  const playBeep = () => {
    setBeepActive(true)
    beepRef.current.currentTime = 0
    beepRef.current.play()
  }

  useEffect(() => {
    let timeout = null

    if (timerActive) {
      if (state.timer === 0) {
        playBeep()
      }

      timeout = setTimeout(() => {
        if ((state.timer - 1) < 0) {
          clearTimeout(timeout)
          dispatch(changeActiveCounter())
          return
        }

        dispatch(countDown())
      }, 1000)
    } else {
      clearTimeout(timeout)
    }

    return () => clearTimeout(timeout)
  }, [timerActive, state, dispatch, changeActiveCounter, countDown])

  useEffect(() => {
    let beepTimeout = null
    if (beepActive) {
      beepTimeout = setTimeout(() => {
        setBeepActive(false)
      }, 1800)
    }
    return () => clearTimeout(beepTimeout)
  }, [beepRef, beepActive])


  return (
    <Container className={classes.container}>
      <audio id="beep" src={timerBeep} ref={beepRef} />
      <Box className={classes.titles}>
        <Typography variant='h3' component='h1'>
          Lycopersicum
        </Typography>
        <Typography variant='h6' component='h2'>
          ~ a pomodoro timer ~
        </Typography>
      </Box>
      <Box className={classes.timer}>
        {state.activeCounter === 'session' ?
          <WorkIcon className={classes.timerLabel} /> :
          <LocalCafeIcon className={classes.timerLabel} />
        }
        <Typography className={classes.timerDisplay}>
          {getMinutes()}
          <span className={clsx({ [classes.timerActive]: timerActive, })}>:</span>
          {getSeconds()}
        </Typography>
      </Box>
      <Container className={classes.sessionControls}>
        <SlotControl
          classes={classes}
          onClickRemove={e => dispatch(decreaseLength('session'))}
          onClickAdd={e => dispatch(increaseLength('session'))}
          label={state.session / 60}
          labelIcon={<WorkIcon fontSize='large' />}
        />
        <SlotControl
          classes={classes}
          onClickRemove={e => dispatch(decreaseLength('break'))}
          onClickAdd={e => dispatch(increaseLength('break'))}
          label={state.break / 60}
          labelIcon={<LocalCafeIcon fontSize='large' />}
        />
      </Container>
      <TimerControls
        classes={classes}
        timerActive={timerActive}
        beepActive={beepActive}
        onClickPlay={e => setTimerActive(true)}
        onClickPause={e => setTimerActive(false)}
        onClickReset={e => {
          setTimerActive(false)
          dispatch(resetCounter())
        }}
      />
    </Container>
  )
}

export default Lycopersicum